import { catchError, first, map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import moment from 'moment';
import { UnitMeasurement } from 'src/assets/data/enum/unit_measurement.enum';
import { ContainerService } from './container.service';
import { ContainerActivity } from '../_models';
import { environment } from '../../../environments/environment';
import { GeneralUtilsService } from './common/generalUtils.service';
import { AppState } from '../_reducers';
import {
  selectAllContainerActivities,
  selectContainerActivitiesWithStatus,
} from '../_selectors/containerActivity.selectors';
import { DepositActivityName } from '../_models/depositActivityName';
import { DepositActivityMovement } from '../_models/depositActivityMovement';
import { TagService } from './tag.service';
import { FilterItem, OrderBy, PaginatedResponse } from '../_models/paginatedResponse';

export interface DateRange {
  start: Date;
  end: Date;
}

export interface DateRange {
  start: Date;
  end: Date;
}

// interfaccia per gestire le informazioni usate nel componente app-x-silos-level
export interface silosUse {
  purchasedPercentage: number; // percentuale riferita alla categoria "Acquistato" nella lista delle info del componente app-x-silos-level
  selfProducedPercentage: number; // percentuale riferita alla categoria "Autoprodotto" nella lista delle info del componente app-x-silos-level
  emptyPercentage: number; // !!! non visibile in GUI >> percentuale riferita alla categoria "VUOTO" nella lista delle info del componente app-x-silos-level
  purchasedTotal: number; // totale riferito alla categoria "Acquistato" nella lista delle info del componente app-x-silos-level
  selfProducedTotal: number; // totale riferito alla categoria "Autoprodotto" nella lista delle info del componente app-x-silos-level
  emptyTotal: number; // !!! non visibile in GUI >> totale riferito alla categoria "VUOTO" nella lista delle info del componente app-x-silos-level
  containerId: number; // riferimento all'id del container
  warehouseId: number; // riferimento all'id del warehouse >>> usato per filtrare tutti i container di un area e fare la media dei valori purchasedPercentage e selfProducedPercentage di tutti i container di quell'area
  walletContainer: number; // sezione "Saldo" nella lista delle info del componente app-x-silos-level
  purchasedPercentageUILevelText: number; // "Acquistato" > percetuale nel riquadro viola che va da 0 a 100 (usato solo come testo dimostrativo)
  selfProducedPercentageUILevelText: number; // "Autoprodotto" > percetuale nel riquadro viola che va da 0 a 100 (usato solo come testo dimostrativo)
  emptyPercentageUILevelText: number; // "VUOTO" > percetuale nel riquadro grigio che va da 0 a 100 (usato solo come testo dimostrativo)
  purchasedPercentageUILevel: number; // "Acquistato" > percetuale che gestisce l'altezza del riquadro viola
  selfProducedPercentageUILevel: number; // "Autoprodotto" > percetuale che gestisce l'altezza del riquadro verde
  emptyPercentageUILevel: number; // "VUOTO" > percetuale che gestisce l'altezza del riquadro grigio per quei movimenti che non sono ne "Acquistato" ne "Autoprodotto"
}

@Injectable()
export class ContainerActivityService {
  private http = inject(HttpClient);
  private generalUtilsService = inject(GeneralUtilsService);
  private containerService = inject(ContainerService);
  private store = inject(Store<AppState>);
  private tagService = inject(TagService);

  private apiBaseUrl = environment.apiBaseUrl;
  private apiBaseUrlV3 = environment.apiBaseUrl + 'api/private/v3';
  containerActivityList$: Observable<ContainerActivity[]> = this.store.pipe(
    select(selectAllContainerActivities)
  );
  selectContainerActivitiesWithStatus$: Observable<{
    loading: boolean;
    loaded: boolean;
    error: any;
    containerActivities: ContainerActivity[];
  }> = this.store.pipe(select(selectContainerActivitiesWithStatus));
  heightSilosUIComponent = 170; // altezza in px del container

  getContainerActivitiesFromLocalStore(): Observable<ContainerActivity[]> {
    return this.containerActivityList$;
  }

  getContainerActivities(avoidTags: boolean = false): Observable<ContainerActivity[]> {
    const tagQuery = avoidTags ? '' : this.tagService.getTagQuery(false);

    return this.http
      .get<ContainerActivity[]>(`${this.apiBaseUrl}api/private/v2/containerActivities?${tagQuery}`)
      .pipe(
        map((activities) => activities),
        catchError((error) => {
          console.error('Error occurred:', error);
          return throwError(() => new Error('Failed to fetch container activities'));
        })
      );
  }

  getMostRecentActivityTypes(): Observable<string[]> {
    return this.http
      .get<string[]>(this.apiBaseUrl + 'api/private/v1/activities/container/mostRecentTypes')
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching most recent activity types:', error);
          return of([]);
        })
      );
  }

  getContainerActivity(id: number): Observable<ContainerActivity> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/containerActivities/' + id)
      .pipe(map((response) => response));
  }

  saveContainerActivity(containerActivity): Observable<ContainerActivity> {
    // Crea una copia superficiale dell'oggetto containerActivity
    const containerActivityCopy = { ...containerActivity };

    delete containerActivityCopy.lastUpdated;
    delete containerActivityCopy.dateCreated;

    if (containerActivityCopy.container) {
      const idcontainer: any = {
        id: containerActivityCopy.container.id,
      };
      containerActivityCopy.container = idcontainer;
    }

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    containerActivityCopy.companyId = companySelected ? companySelected.id : null;

    const dateFormatted = moment(containerActivityCopy.activityDate)
      .startOf('day')
      .utc()
      .toISOString();
    containerActivityCopy.activityDate = dateFormatted
      ? dateFormatted.replace(':00Z', ':00.000Z')
      : ('' as any);

    if (containerActivityCopy.id) {
      return this.http
        .put<ContainerActivity>(
          this.apiBaseUrl + 'api/private/v1/containerActivities/' + containerActivityCopy.id,
          containerActivityCopy
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<ContainerActivity>(
          this.apiBaseUrl + 'api/private/v1/containerActivities/',
          containerActivityCopy
        )
        .pipe(map((response) => response));
    }
  }

  saveContainerActivity2(containerActivity: any): Observable<ContainerActivity> {
    // Rimuovi le proprietà che non devono essere inviate
    delete containerActivity.lastUpdated;
    delete containerActivity.dateCreated;

    // Gestione del tipo 'mechanical_drying'
    if (containerActivity.type === 'mechanical_drying') {
      containerActivity.movement = containerActivity.id ? containerActivity.movement : 'incoming';
    }

    // Imposta companyId
    const companySelected = JSON.parse(localStorage.getItem('companySelected') || '{}');
    containerActivity.companyId = companySelected ? companySelected.id : null;

    // Formatta la data
    const dateFormatted = moment(containerActivity.activityDate).startOf('day').utc().toISOString();
    containerActivity.activityDate = dateFormatted
      ? dateFormatted.replace(':00Z', ':00.000Z')
      : ('' as any);

    // Controlla se l'attività esiste già o è una nuova
    if (containerActivity.id) {
      return this.http
        .put<ContainerActivity>(
          `${this.apiBaseUrl}api/private/v2/containerActivities/${containerActivity.id}`,
          containerActivity
        )
        .pipe(map((response: ContainerActivity) => response));
    } else {
      return this.http
        .post<ContainerActivity>(
          `${this.apiBaseUrl}api/private/v2/containerActivities/`,
          containerActivity
        )
        .pipe(map((response: ContainerActivity) => response));
    }
  }

  getContainerActivitiesByContainerId = (id: number): Array<ContainerActivity> => {
    let containerActivitiesFiltered: Array<ContainerActivity>;
    this.containerActivityList$
      .pipe(first((list: ContainerActivity[]) => list && list.length > 0))
      .subscribe((list: ContainerActivity[]) => {
        containerActivitiesFiltered = list.filter((i) => i.container && i.container.id === id);
      });
    return containerActivitiesFiltered;
  };

  convertBasedOnUnit(quantity: number, unit): number {
    switch (unit) {
      case UnitMeasurement.T:
      case UnitMeasurement.Q:
      case UnitMeasurement.KG:
        return this.convertToKg(quantity, unit);
      case UnitMeasurement.HL:
      case UnitMeasurement.L:
        return this.convertToL(quantity, unit);
      default:
        return quantity;
    }
  }

  getWalletSizeByContainerId = (id: number): number => {
    let sumsize = 0;

    this.containerActivityList$.subscribe((containerActivities) => {
      if (containerActivities && containerActivities.length > 0) {
        containerActivities.forEach((element) => {
          if (element && element.container && element.container.id === id) {
            const unit = this.getUnitForContainerActivity(element);
            const { quantity } = element;

            // If the type is 'mechanical_drying', we always work in kg
            if (element.type === DepositActivityName.MECHANICAL_DRYING) {
              // Check movement: add if 'incoming', subtract if 'outgoing'
              if (element.movement === DepositActivityMovement.INCOMING) {
                sumsize += quantity;
              } else if (element.movement === DepositActivityMovement.OUTGOING) {
                sumsize -= quantity;
              }
            } else if (element.type === DepositActivityName.DEFENSE) {
              switch (element.movement) {
                case DepositActivityMovement.IN_CONTAINER:
                  break;
                case DepositActivityMovement.INCOMING:
                  sumsize += this.convertBasedOnUnit(quantity, unit);
                  break;
                case DepositActivityMovement.OUTGOING:
                  sumsize -= this.convertBasedOnUnit(quantity, unit);
                  break;
              }
            } else {
              if (
                element.type === DepositActivityName.BUY ||
                element.type === DepositActivityName.INVENTORY ||
                (element.type === DepositActivityName.TANNING &&
                  element.movement === DepositActivityMovement.INCOMING)
              ) {
                switch (unit) {
                  case UnitMeasurement.T:
                  case UnitMeasurement.Q:
                  case UnitMeasurement.KG:
                    sumsize += this.convertToKg(quantity, unit);
                    break;
                  case UnitMeasurement.HL:
                  case UnitMeasurement.L:
                    sumsize += this.convertToL(quantity, unit);
                    break;
                  default:
                    sumsize += quantity;
                }
              } else if (element.movement !== DepositActivityMovement.IN_CONTAINER) {
                switch (unit) {
                  case UnitMeasurement.T:
                  case UnitMeasurement.Q:
                  case UnitMeasurement.KG:
                    sumsize -= this.convertToKg(quantity, unit);
                    break;
                  case UnitMeasurement.HL:
                  case UnitMeasurement.L:
                    sumsize -= this.convertToL(quantity, unit);
                    break;
                  default:
                    sumsize -= quantity;
                }
              }
            }
          }
        });
      }
    });

    return sumsize;
  };

  getWalletSizeByContainerIdAndCategoryType = (id: number, type: string): number => {
    let sumsize = 0;
    this.containerActivityList$.subscribe((containerActivities) => {
      if (containerActivities && containerActivities.length > 0) {
        let containerActivitiesFiltered = [];
        if (type) {
          containerActivitiesFiltered = (containerActivities ?? []).filter(
            (ca) => ca.category && ca.category == type
          );
        } else {
          containerActivitiesFiltered = (containerActivities ?? []).filter(
            (ca) =>
              !ca.category ||
              ca.category == null ||
              ca.category == undefined ||
              ca.category == 'sale'
          );
        }
        containerActivitiesFiltered.forEach((element) => {
          if (element && element.container && element.container.id === id) {
            const unit = this.getUnitForContainerActivity(element);
            // Quantity of the activity
            if (element.type === 'buy' || element.type === 'inventory') {
              sumsize += this.convertToKg(Number(element.quantity), unit);
            } else {
              sumsize -= this.convertToKg(Number(element.quantity), unit);
            }
          }
        });
      }
    });
    return sumsize;
  };

  deleteContainerActivity(id: number): Observable<ContainerActivity> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v2/containerActivities/' + id).pipe(
      map(() => {
        return { id } as ContainerActivity;
      })
    );
  }

  getExportType = (): Array<any> => {
    const result: Array<any> = [
      {
        type: 'silos',
        subtype: 'silos_selfmade',
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'silos',
        subtype: 'silos_buy',
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
    ];
    return result;
  };

  getContainerActivitiesByCostProductId = (id: number): Array<ContainerActivity> => {
    let containerActivitiesFiltered: Array<ContainerActivity>;
    this.containerActivityList$.subscribe((containerActivities) => {
      containerActivitiesFiltered = containerActivities.filter((f: any) =>
        f.externalResources?.find(
          (e: any) => e.externalId == id && e.externalSource == 'finance_service'
        )
      );
    });
    return containerActivitiesFiltered;
  };

  calculateProduct = (containerId: number, warehouseId: number): silosUse => {
    const walletContainer = this.generalUtilsService.roundCustom(
      this.getWalletSizeByContainerId(containerId),
      2
    );
    let purchasedTotal = this.purchasedContainerActivity(containerId);
    let selfProducedTotal = this.selfProductContainerActivity(containerId);
    const emptyTotal = this.emptyContainerActivity(containerId);

    let percentageDetraction = 0;
    // Calcolo della percentuale di detrazione
    if (purchasedTotal + selfProducedTotal === 0) {
      percentageDetraction = 0;
    } else {
      if (emptyTotal < 0) {
        percentageDetraction = (Math.abs(emptyTotal) / (purchasedTotal + selfProducedTotal)) * 100;
      }
    }

    // Calcolo della detrazione per purchasedTotal
    const purchasedTotalDetraction = purchasedTotal * (percentageDetraction / 100);

    // Calcolo della detrazione per selfProducedTotal
    const selfProducedTotalDetraction = selfProducedTotal * (percentageDetraction / 100);

    // Calcolo dei guadagni netti rimanenti per purchasedTotal e selfProducedTotal
    purchasedTotal = purchasedTotal - purchasedTotalDetraction;
    selfProducedTotal = selfProducedTotal - selfProducedTotalDetraction;

    const sumTotal = purchasedTotal + selfProducedTotal;
    const container = this.containerService.getLocalContainerById(containerId);
    const maxCapacity = container.maxCapacity ? container.maxCapacity : 0;
    let scala = 0;
    if (maxCapacity === 0 && sumTotal === 0) {
      scala = 0;
    } else {
      scala = Math.min(1, maxCapacity / sumTotal);
    }
    const purchasedPercentage = this.purchasedNetContainerActivityPercentage(
      purchasedTotal,
      walletContainer,
      scala,
      false
    );
    const selfProducedPercentage = this.selfNetProductContainerActivityPercentage(
      selfProducedTotal,
      walletContainer,
      scala,
      false
    );
    const emptyPercentage = this.emptyNetContainerActivity(
      emptyTotal,
      walletContainer,
      scala,
      false
    );

    const purchasedPercentageUILevelText = this.purchasedContainerActivityPercentage(
      purchasedTotal,
      maxCapacity,
      scala,
      false
    );
    const selfProducedPercentageUILevelText = this.selfProductContainerActivityPercentage(
      selfProducedTotal,
      maxCapacity,
      scala,
      false
    );
    const emptyPercentageUILevelText = this.emptyContainerActivityPercentage(
      emptyTotal,
      maxCapacity,
      scala,
      false
    );

    const purchasedPercentageUILevel = this.purchasedContainerActivityPercentage(
      purchasedTotal,
      maxCapacity,
      scala,
      false
    );
    const selfProducedPercentageUILevel = this.selfProductContainerActivityPercentage(
      selfProducedTotal,
      maxCapacity,
      scala,
      false
    );
    const emptyPercentageUILevel = this.emptyContainerActivityPercentage(
      emptyTotal,
      maxCapacity,
      scala,
      false
    );

    return {
      walletContainer,
      purchasedPercentage,
      selfProducedPercentage,
      emptyPercentage: emptyPercentage < 0 ? 0 : emptyPercentage,

      purchasedPercentageUILevel,
      selfProducedPercentageUILevel,
      emptyPercentageUILevel: this.blockMaxCapacityUILevel(emptyPercentageUILevel),

      purchasedPercentageUILevelText,
      selfProducedPercentageUILevelText,
      emptyPercentageUILevelText,

      purchasedTotal: this.generalUtilsService.roundCustom(purchasedTotal, 2),
      selfProducedTotal: this.generalUtilsService.roundCustom(selfProducedTotal, 2),
      emptyTotal,
      containerId,
      warehouseId,
    };
  };

  purchasedNetContainerActivityPercentage(purchasedTotal, total, scala, isResize0To100 = true) {
    if (total > 0) {
      if (isResize0To100) {
        return this.generalUtilsService.roundCustom(
          this.resize0To100((purchasedTotal / total) * 100), //this.heightSilosUIComponent * scala),
          2
        );
      } else {
        return this.generalUtilsService.roundCustom(
          (purchasedTotal / total) * 100, //this.heightSilosUIComponent * scala,
          2
        );
      }
    } else {
      return 0;
    }
  }

  selfNetProductContainerActivityPercentage(
    selfProducedTotal,
    total,
    scala,
    isResize0To100 = true
  ) {
    if (total > 0) {
      if (isResize0To100) {
        return this.generalUtilsService.roundCustom(
          this.resize0To100((selfProducedTotal / total) * 100), //this.heightSilosUIComponent * scala),
          2
        );
      } else {
        return this.generalUtilsService.roundCustom(
          (selfProducedTotal / total) * 100, //this.heightSilosUIComponent * scala,
          2
        );
      }
    } else {
      return 0;
    }
  }

  emptyNetContainerActivity(emptyTotal, total, scala, isResize0To100 = true) {
    if (total > 0) {
      if (isResize0To100) {
        return this.generalUtilsService.roundCustom(
          this.resize0To100((emptyTotal / total) * 100), //this.heightSilosUIComponent * scala),
          2
        );
      } else {
        return this.generalUtilsService.roundCustom(
          (emptyTotal / total) * 100, //this.heightSilosUIComponent * scala,
          2
        );
      }
    } else {
      return 0;
    }
  }

  purchasedContainerActivity(containerId: number): number {
    const containerActivities = this.getContainerActivitiesByContainerId(containerId);
    if (containerActivities && containerActivities.length > 0) {
      const purchasedQty = this.getWalletSizeByContainerIdAndCategoryType(containerId, 'purchase');
      if (purchasedQty && purchasedQty > 0) {
        return purchasedQty;
      }
      return 0;
    }
    return 0;
  }

  emptyContainerActivity(containerId: number): number {
    const containerActivities = this.getContainerActivitiesByContainerId(containerId);
    if (containerActivities && containerActivities.length > 0) {
      const purchasedQty = this.getWalletSizeByContainerIdAndCategoryType(containerId, null);
      if (purchasedQty) {
        return purchasedQty;
      }
      return 0;
    }
    return 0;
  }

  selfProductContainerActivity(containerId: number): number {
    const containerActivities = this.getContainerActivitiesByContainerId(containerId);
    if (containerActivities && containerActivities.length > 0) {
      const purchasedQty = this.getWalletSizeByContainerIdAndCategoryType(
        containerId,
        'self_produced'
      );
      if (purchasedQty && purchasedQty > 0) {
        return purchasedQty;
      }
      return 0;
    }
    return 0;
  }

  selfProductContainerActivityPercentage(selfProducedTotal, total, scala, isResize0To100 = true) {
    if (selfProducedTotal < 0) {
      return 0;
    }
    if (isResize0To100) {
      return this.generalUtilsService.roundCustom(
        this.resize0To100((selfProducedTotal / total) * 100), //this.heightSilosUIComponent * scala),
        2
      );
    } else {
      return this.generalUtilsService.roundCustom(
        (selfProducedTotal / total) * 100, //this.heightSilosUIComponent * scala,
        2
      );
    }
  }

  emptyContainerActivityPercentage(emptyTotal, total, scala, isResize0To100 = true) {
    if (emptyTotal < 0) {
      return 0;
    }
    if (isResize0To100) {
      return this.generalUtilsService.roundCustom(
        this.resize0To100((emptyTotal / total) * 100), //this.heightSilosUIComponent * scala),
        2
      );
    } else {
      return this.generalUtilsService.roundCustom(
        (emptyTotal / total) * 100, //this.heightSilosUIComponent * scala,
        2
      );
    }
  }

  purchasedContainerActivityPercentage(purchasedTotal, total, scala, isResize0To100 = true) {
    if (purchasedTotal < 0) {
      return 0;
    }
    if (isResize0To100) {
      return this.generalUtilsService.roundCustom(
        this.resize0To100((purchasedTotal / total) * 100), //this.heightSilosUIComponent * scala),
        2
      );
    } else {
      return this.generalUtilsService.roundCustom(
        (purchasedTotal / total) * 100, //this.heightSilosUIComponent * scala,
        2
      );
    }
  }

  resize0To100(total) {
    return (total * 100) / this.heightSilosUIComponent;
  }

  blockMaxCapacityUILevel(total) {
    return total > this.heightSilosUIComponent ? this.heightSilosUIComponent : total;
  }

  getUnitForContainerActivity(activity: ContainerActivity): string {
    if (activity.unit) {
      return activity.unit;
    }
    const container = this.containerService.getLocalContainerById(activity.container.id);
    return container.unit as string;
  }

  convertToKg(quantity: number, unit: string): number {
    const conversionRates = {
      [UnitMeasurement.T]: 1000,
      [UnitMeasurement.Q]: 100,
      [UnitMeasurement.KG]: 1,
    };

    return quantity * (conversionRates[unit] || 1);
  }

  convertToL(quantity: number, unit: string): number {
    const conversionRates = {
      [UnitMeasurement.HL]: 100,
      [UnitMeasurement.L]: 1,
    };

    return quantity * (conversionRates[unit] || 1);
  }

  /**
   * Fetches paginated container activities with optional filters, sorting and search.
   *
   * @param params.number Page number starting from 0!
   * @param params.max Number of items per page
   * @param params.textSearch Free text search
   * @param params.year Filter activities by year
   * @param params.filterToApply Additional filters to apply. Backend accepts: containerId, warehouseId, id, productId, tags
   * @param params.orderBy Sorting configuration
   * @returns Observable with paginated response
   */
  getContainerActivitiesPaged(params: {
    number?: number;
    max?: number;
    textSearch?: string;
    filterToApply?: FilterItem[];
    orderBy?: OrderBy[];
  }): Observable<PaginatedResponse<ContainerActivity[]>> {
    const companyId = JSON.parse(localStorage.getItem('companySelected') ?? 'null')?.id;
    const selectedTags = this.tagService.selectedInternalTagNamesSubject?.getValue() ?? [];

    const payload = {
      ...(params.number != null && { number: params.number }),
      ...(params.max != null && { max: params.max }),
      ...(params.textSearch?.trim() && { textSearch: params.textSearch.trim() }),
      sort: {
        orderBy: params.orderBy ?? [{ property: 'activityDate', direction: 'DESC' }],
      },
      filter: {
        filters: [
          ...(params.filterToApply ?? []),
          ...(selectedTags.length ? selectedTags.map((tag) => ({ name: 'tags', value: tag })) : []),
          { name: 'companyId', value: companyId },
        ],
      },
    };

    return this.http.post<PaginatedResponse<ContainerActivity[]>>(
      `${this.apiBaseUrlV3}/containerActivities`,
      payload
    );
  }
}
